/// <amd-module name="Analytics/Common/Utilities/BatchResponseUtilities" />
import * as Resources from "Analytics/Common/Resources";

export async function extractBatchPayloadResult(
    response: Response | null,
    getBatchResponseInnerHeaders?: boolean
): Promise<number | object | boolean | null> {
    if (response == null) {
        throw new Error(Resources.ODataResponseNotFoundErrorText);
    }

    const batchId = extractBatchId(response.headers);
    // If header is well-formed, process the body
    const payloadText = await response.text();
    if (payloadText == null || payloadText === "") {
        throw new Error(Resources.ODataPayloadBodyNotFoundErrorText);
    }

    const batchResponseParser = new BatchResponseParser(payloadText, batchId);
    const rawData = batchResponseParser.getResponseContent();

    const formattedResult = rawData ? formatResult(rawData) : null;
    throwIfResultIsError(formattedResult);
    if (getBatchResponseInnerHeaders) {
        const responseHeaders = batchResponseParser.getResponseHeaders();
        const responseStatus = batchResponseParser.getResponseLine();
        if (typeof formattedResult === "object") {
            return {
                responseStatus,
                responseHeaders,
                ...formattedResult
            };
        } else {
            return {
                responseStatus,
                responseHeaders,
                value: formattedResult
            }
        }
    }

    return formattedResult;
}

/** Extract unique ID for Batch ID from Response Header. If not present, the read cannot move forward. */
function extractBatchId(headers: Headers): string {
    if (headers == null) {
        throw new Error(Resources.ODataHeaderNotFoundErrorText);
    }

    const headerContentType = headers.get("Content-Type");
    if (headerContentType == null) {
        throw new Error(Resources.ODataHeaderNotFoundErrorText);
    }
    const responseStatusRegex: RegExp = new RegExp("^multipart/mixed; boundary=(.*)", "i");
    const batchIdMatch = responseStatusRegex.exec(headerContentType);
    if (batchIdMatch == null) {
        throw new Error(Resources.ODataHeaderTypeUnrecognizedErrorText);
    }
    return batchIdMatch[1];
}

function formatResult(rawData: string): number | object | boolean | null {
    const countResult = parseInt(rawData);
    if (countResult) {
        return countResult;
    } else {
        return JSON.parse(rawData);
    }
}

function throwIfResultIsError(result: number | object | boolean | null): void | never {
    if (typeof result === "object" && result != null && typeof (result as any).error !== "undefined") {
        throw result;
    }
}

/**
    Very simple batch response parser. Only processes the first operation's response.

    Expects well formed batch responses like:
        --batchresponse_08764fd4-3946-4cd6-97cf-1ec7d9740551
        Content-Type: application/http
        Content-Transfer-Encoding: binary

        HTTP/1.1 200 OK
        Content-Type: application/json; odata.metadata=minimal; odata.streaming=true
        OData-Version: 4.0

        {"@odata.context":"https://app.me.tfsallin.net:43526/_odata/$metadata#Projects","value":[{"ProjectSK":"ed2c5089-ffba-4ffc-a6cb-3fcdc0cef924","ProjectId":"ed2c5089-ffba-4ffc-a6cb-3fcdc0cef924","ProjectName":"ODataBatchTest"}]}
        --batchresponse_08764fd4-3946-4cd6-97cf-1ec7d9740551
*/
class BatchResponseParser {
    private static ResponseStatusRegex: RegExp = /^HTTP\/1\.\d (\d{3}) (.*)/i;
    private static ResponseHeaderRegex: RegExp = /^([^()<>@,;:\\"\/[\]?={} \t]+)\s?:\s?(.*)/;

    // instance vars
    private position: number = 0;
    private content: string;
    private batchSeparator: string;
    private responseLine: any; // {StatusCode: int, StatusText: string}
    private responseHeaders: any; // {Content-Type: "", OData-Version: ""}
    private responseContent: string;

    // supply the raw response payload content along with the batch separator (usually a guid like 08764fd4-3946-4cd6-97cf-1ec7d9740551 in example above)
    constructor(content: string, batchSeparator: string) {
        this.content = content;
        this.batchSeparator = batchSeparator;

        this.parseRawResponse();
    }

    // getters
    public getResponseLine(): any {
        return this.responseLine;
    }
    public getResponseHeaders(): any {
        return this.responseHeaders;
    }
    public getResponseContent(): string {
        return this.responseContent;
    }

    // read and return the content from current position until next instance of {until}
    private readTo(until: string): string {
        const start = this.position;
        const end = this.content.indexOf(until, this.position);
        if (end === -1) {
            return "";
        }
        this.position = end + until.length;

        return this.content.substring(start, end);
    }

    private readLine(): string {
        return this.readTo("\r\n");
    }

    /**
     * Parses the raw response content and sets the responseLine, responseHeader, and responseContent values
     */
    private parseRawResponse(): any {
        const responseSeparator = `--${this.batchSeparator}`;
        // read past the start separator
        this.readTo(responseSeparator);

        // read through the emtpy line before the response line skipping batch response headers.
        this.readTo("\r\n\r\n");

        this.responseLine = this.readResponseLine();
        this.responseHeaders = this.readResponseHeaders();

        // read the emtpy line before the response body
        this.readLine();

        this.responseContent = this.readLine();
    }

    // returns response line properties { StatusCode: int, StatusText: string } or null
    private readResponseLine(): any {
        const start = this.position;
        const resLine = this.readLine();
        const match = BatchResponseParser.ResponseStatusRegex.exec(resLine);
        if (match) {
            return { StatusCode: match[1], StatusText: match[2] };
        } else {
            this.position = start; // whatever we read was not the status line
        }
        return null;
    }

    // returns response headers like {Content-Type: string, OData-Version: string}
    private readResponseHeaders(): any {
        const headers: any = {};
        let start = this.position;
        let line: string;
        let match;

        do {
            start = this.position;
            line = this.readLine();
            match = BatchResponseParser.ResponseHeaderRegex.exec(line);
            if (match !== null) {
                headers[match[1]] = match[2];
            } else {
                this.position = start; // whatever we read was not a header line
            }
        } while (line && match);

        return headers;
    }
}
